// src/features/inicio/pages/DashboardInicioPage.jsx
import { useCallback, useEffect, useMemo } from "react";
import { Box, ButtonBase, Chip, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import GroupIcon from "@mui/icons-material/Group";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { useEmpresa } from "../../../app/providers/EmpresaProvider";
import InicioApi from "../api/inicioApi";

import Ilustracion from "../../../assets/Ilustracion.svg";

const inicioAreas = [
  {
    icon: <GroupIcon />,
    label: "Recursos Humanos",
    description:
      "Accedé a la administración de empleados, horarios, visualizador de asistencia, tareas, liquidación, mensajería y contenido del equipo.",
    to: "/rrhh/dashboard",
    disabled: false,
  },
  {
    icon: <Inventory2Icon />,
    label: "Stock",
    description:
      "Administración de inventario, productos, movimientos y control de existencias.",
    to: "/stock",
    disabled: false,
  },
  {
    icon: <AccountBalanceWalletIcon />,
    label: "Tesorería",
    description:
      "Gestioná cierres, movimientos, cuentas, caja y control financiero.",
    to: "/tesoreria",
    disabled: false,
  },
];

function InicioAreasGrid({ areas, onAreaClick }) {
  return (
    <Stack
      spacing={1.5}
      sx={{
        p: 2,
        bgcolor: "rgba(244, 245, 251, 0.88)",
        borderRadius: "24px",
        border: "1px solid rgba(255,255,255,0.8)",
        boxShadow:
          "inset 0px 0px 5px rgba(117, 117, 138, 0.25), 0px 10px 28px rgba(27, 37, 89, 0.08)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "fit-content",
          px: 2,
          py: 1,
          bgcolor: "primary.500",
          borderRadius: "33px",
          boxShadow:
            "inset 0px 0px 3px rgba(192, 192, 201, 0.5), 0px 5px 14px rgba(16, 47, 148, 0.28)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            color: "logoblanco",
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          Módulos a acceder
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        {areas.map((area) => {
          const disabled = Boolean(area.disabled);

          return (
            <ButtonBase
              key={area.label}
              aria-disabled={disabled}
              onClick={!disabled ? () => onAreaClick?.(area) : undefined}
              sx={{
                width: "100%",
                minHeight: { xs: 180, md: 220 },
                borderRadius: "20px",
                textAlign: "left",
                alignItems: "stretch",
                justifyContent: "stretch",
                bgcolor: "rgba(255,255,255,0.78)",
                border: "1px solid rgba(255,255,255,0.92)",
                boxShadow:
                  "0px 8px 20px rgba(27, 37, 89, 0.08), inset 0px 0px 3px rgba(142, 142, 159, 0.18)",
                overflow: "hidden",
                position: "relative",
                opacity: disabled ? 0.62 : 1,
                filter: disabled ? "grayscale(0.12)" : "none",
                cursor: disabled ? "not-allowed" : "pointer",
                transition:
                  "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle at 18% 15%, rgba(29, 70, 183, 0.10), transparent 32%)",
                  opacity: 0,
                  transition: "opacity 180ms ease",
                },
                "&:hover": disabled
                  ? {}
                  : {
                      transform: "translateY(-6px)",
                      bgcolor: "rgba(248, 250, 255, 0.96)",
                      borderColor: "rgba(35, 78, 190, 0.35)",
                      boxShadow:
                        "0px 14px 30px rgba(27, 37, 89, 0.16), inset 0px 0px 3px rgba(142, 142, 159, 0.18)",
                    },
                "&:hover::before": disabled ? {} : { opacity: 1 },
                "&:hover .inicioAreaIcon": disabled
                  ? {}
                  : {
                      transform: "scale(1.06) translateY(-2px)",
                      bgcolor: "rgba(30, 68, 177, 0.13)",
                    },
                "&:hover .inicioAreaTitle": disabled
                  ? {}
                  : {
                      color: "primary.700",
                      transform: "translateY(-2px)",
                    },
                "&:hover .inicioAreaDescription": disabled
                  ? {}
                  : {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                "&:hover .inicioAreaArrow": disabled
                  ? {}
                  : {
                      bgcolor: "primary.600",
                      color: "#fff",
                      transform: "translateX(4px)",
                      boxShadow: "0px 8px 18px rgba(16, 47, 148, 0.32)",
                    },
                "&:hover .inicioAreaBottomLine": disabled
                  ? {}
                  : {
                      width: "36%",
                      opacity: 1,
                    },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {disabled ? (
                  <Chip
                    label={area.badge || "Próximamente"}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      zIndex: 2,
                      borderRadius: "10px",
                      bgcolor: "neutral.200",
                      color: "primary.900",
                      fontSize: 10.5,
                      fontWeight: 900,
                    }}
                  />
                ) : null}

                <Box
                  className="inicioAreaIcon"
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "50%",
                    bgcolor: "rgba(30, 68, 177, 0.10)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "primary.700",
                    mb: 2,
                    boxShadow: "inset 0px 0px 4px rgba(30, 68, 177, 0.08)",
                    transition: "transform 180ms ease, background 180ms ease",
                    "& svg": {
                      width: 32,
                      height: 32,
                    },
                  }}
                >
                  {area.icon}
                </Box>

                <Typography
                  className="inicioAreaTitle"
                  sx={{
                    fontWeight: 800,
                    color: "rgba(18, 27, 61, 0.95)",
                    fontSize: 16,
                    lineHeight: 1,
                    mb: 1,
                    textTransform: "none",
                    transition: "transform 180ms ease, color 180ms ease",
                  }}
                >
                  {area.label}
                </Typography>

                <Typography
                  className="inicioAreaDescription"
                  sx={{
                    fontWeight: 500,
                    color: "rgba(19, 27, 58, 0.82)",
                    fontSize: 13,
                    lineHeight: 1.2,
                    opacity: 0.9,
                    transform: "translateY(2px)",
                    transition: "opacity 180ms ease, transform 180ms ease",
                    maxWidth: "92%",
                  }}
                >
                  {area.description}
                </Typography>

                <Box sx={{ flexGrow: 1 }} />

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <Box
                    className="inicioAreaArrow"
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      bgcolor: disabled
                        ? "rgba(235,235,240,0.9)"
                        : "rgba(255,255,255,0.9)",
                      color: disabled ? "neutral.500" : "primary.800",
                      border: "1px solid rgba(20, 32, 82, 0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition:
                        "transform 180ms ease, background 180ms ease, color 180ms ease, box-shadow 180ms ease",
                    }}
                  >
                    <ArrowForwardIcon sx={{ fontSize: 23 }} />
                  </Box>
                </Box>

                <Box
                  className="inicioAreaBottomLine"
                  sx={{
                    position: "absolute",
                    left: 22,
                    bottom: 0,
                    width: 0,
                    height: 4,
                    borderRadius: "99px 99px 0 0",
                    bgcolor: "primary.600",
                    opacity: 0,
                    transition: "width 180ms ease, opacity 180ms ease",
                  }}
                />
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Stack>
  );
}

export default function DashboardInicioPage() {
  const navigate = useNavigate();
  const { empresaNombre, empresaId, setEmpresaNombre } = useEmpresa();

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!empresaId) return;
      if ((empresaNombre || "").trim()) return;

      try {
        const empresa = await InicioApi.getEmpresaById(empresaId);
        const nombre = (empresa?.name || empresa?.nombre || "").trim();
        if (alive && nombre) setEmpresaNombre(nombre);
      } catch {
        // si falla, no rompemos la UI
      }
    })();

    return () => {
      alive = false;
    };
  }, [empresaId, empresaNombre, setEmpresaNombre]);

  const empresaLabel = useMemo(() => {
    const n = (empresaNombre || "").trim();
    if (n) return n;
    if (empresaId) return `Empresa #${empresaId}`;
    return "tu empresa";
  }, [empresaNombre, empresaId]);

  const handleAreaClick = useCallback(
    (area) => {
      if (area?.disabled || !area?.to) return;
      navigate(area.to);
    },
    [navigate]
  );

  return (
    <Box p={2} sx={{ width: "100%" }}>
      <Stack spacing={2}>
        <Box
          sx={{
            width: "100%",
            borderRadius: "18px",
            overflow: "hidden",
            position: "relative",
            border: "1px solid rgba(96,96,113,0.18)",
            boxShadow:
              "inset 0px 0px 4px rgba(117,117,138,0.35), 0px 10px 30px rgba(0,0,0,0.06)",
            background:
              "linear-gradient(135deg, rgba(191,198,225,1) 0%, rgba(175,169,223,1) 55%, rgba(162,218,231,0.9) 100%)",
            px: { xs: 2, sm: 3 },
            py: { xs: 2.25, sm: 3 },
            minHeight: { xs: 140, sm: 160 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(closest-side at 20% 30%, rgba(204,116,192,0.22), transparent 55%)," +
                "radial-gradient(closest-side at 80% 20%, rgba(14,50,149,0.18), transparent 50%)," +
                "radial-gradient(closest-side at 85% 85%, rgba(82,105,173,0.18), transparent 55%)",
              pointerEvents: "none",
            },
          }}
        >
          <Stack spacing={0.2} sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              sx={{
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 600,
                color: "rgba(2,8,23,1)",
              }}
            >
              Bienvenido,
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 700,
                color: "rgba(2,8,23,1)",
              }}
            >
              estás en {empresaLabel}
            </Typography>
          </Stack>

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              height: { xs: 120, sm: 160 },
              width: { xs: 150, sm: 240 },
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              ml: 2,
            }}
          >
            <Box
              component="img"
              src={Ilustracion}
              alt="Ilustración"
              sx={{
                height: "120%",
                width: "100%",
                objectFit: "contain",
                marginTop: "-10px",
                marginBottom: "-10px",
              }}
            />
          </Box>
        </Box>

        <InicioAreasGrid areas={inicioAreas} onAreaClick={handleAreaClick} />
      </Stack>
    </Box>
  );
}
